import {
  ENTITY_ACCESS_RIGHTS,
  HTTP_STATUS_CODE,
  QUERY_STATUS,
} from "constants/constants";
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  createSubcontractorQuery,
  deleteSubcontractorQuery,
  getSubcontractorBySlugQuery,
  getSubcontractorQuery,
  updateSubcontractorQuery,
  getSubcontractorsBySubcontractorCompanyQuery,
} from "./queries";

import { withUserScopeCheck } from "utils/helper";
import { useUserContext } from "api/user/UserProvider";
import { extractApiFieldErrorsFlat } from "utils/apiError";

const SubcontractorContext = createContext({});

export const useSubcontractorContext = () => useContext(SubcontractorContext);

export const SubcontractorProvider = ({ ...props }) => {
  const { apiErrorResponse, setApiErrorResponse } = useUserContext();
  const [createdSubcontractor, setCreatedSubcontractor] = useState({});
  const [createdSubcontractorStatus, setCreatedSubcontractorStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [subcontractorById, setSubcontractorById] = useState({});
  const [headerRoles, setHeaderRoles] = useState([]);
  const [editSubcontractorStatus, setEditSubcontractorStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [deleteSubcontractorStatus, setDeleteSubcontractorStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [displaySubcontractorStatus, setDisplaySubcontractorStatus] = useState(
    QUERY_STATUS.INITIAL
  );
  const [subcontractorBySlug, setSubcontractorBySlug] = useState({});
  const lastSubcontractorSlugRef = useRef(null);
  const [
    subcontractorsBySubcontractorCompany,
    setSubcontractorsBySubcontractorCompany,
  ] = useState([]);
  const [subcontractorAccessRights, setSubcontractorAccessRights] = useState(
    []
  );

  const { userScope } = useUserContext();

  const getSubcontractorBySlug = useCallback(
    async (slug) => {
      if (slug !== lastSubcontractorSlugRef.current) {
        setDisplaySubcontractorStatus(QUERY_STATUS.LOADING);
        lastSubcontractorSlugRef.current = slug;
      }
      setSubcontractorAccessRights([]);
      try {
        const result = await withUserScopeCheck({
          callback: async () => {
            return await getSubcontractorBySlugQuery({
              slug,
              userScope: userScope,
            });
          },
          scope: userScope,
        });
        const hasGetAccessRights = result?.access.includes(
          ENTITY_ACCESS_RIGHTS.GET
        );
        if (hasGetAccessRights) {
          setSubcontractorBySlug(result?.subcontractor);
          setDisplaySubcontractorStatus(QUERY_STATUS.SUCCESS);
          setSubcontractorAccessRights(result?.access);
        }
      } catch (e) {
        if (e.response?.status === HTTP_STATUS_CODE.NOT_FOUND) {
          setDisplaySubcontractorStatus(QUERY_STATUS.NOT_FOUND);
        } else {
          setDisplaySubcontractorStatus(QUERY_STATUS.ERROR);
        }
        console.error("getSubcontractorBySlug", e);
        throw e;
      }
    },
    [userScope]
  );

  const subcontractorCreate = useCallback(
    async (data) => {
      setApiErrorResponse(null);
      setCreatedSubcontractorStatus(QUERY_STATUS.LOADING);
      try {
        const result = await withUserScopeCheck({
          callback: async () => {
            return await createSubcontractorQuery({
              data,
              userScope: userScope,
            });
          },
          scope: userScope,
        });
        if (result) {
          setCreatedSubcontractorStatus(QUERY_STATUS.SUCCESS);
          setCreatedSubcontractor(result?.subcontractor);
        }
      } catch (e) {
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        e.response.data.message === "DUPLICATE_EMAIL"
          ? setCreatedSubcontractorStatus(QUERY_STATUS.DUPLICATE_EMAIL)
          : setCreatedSubcontractorStatus(QUERY_STATUS.ERROR);
        console.error("subcontractorCreate", e);
        throw e;
      }
    },
    [userScope]
  );

  const subcontractorByIdGetter = useCallback(
    async (id) => {
      try {
        const result = await withUserScopeCheck({
          callback: async () => {
            return await getSubcontractorQuery({
              id,
              userScope: userScope,
            });
          },
          scope: userScope,
        });
        setSubcontractorById(result?.subcontractor);
        setDisplaySubcontractorStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setDisplaySubcontractorStatus(QUERY_STATUS.ERROR);

        console.error("subcontractorByIdGetter", e);
        throw e;
      }
    },
    [setSubcontractorById, userScope]
  );

  const subStrUpdate = useCallback(
    async (id, data) => {
      setApiErrorResponse(null);
      setEditSubcontractorStatus(QUERY_STATUS.LOADING);
      try {
        const result = await withUserScopeCheck({
          callback: async () => {
            return await updateSubcontractorQuery({
              id,
              data,
              userScope,
            });
          },
          scope: userScope,
        });
        setSubcontractorById(result?.subcontractor);
        setEditSubcontractorStatus(QUERY_STATUS.SUCCESS);
      } catch (e) {
        setApiErrorResponse(
          extractApiFieldErrorsFlat(e?.response?.data?.["bad-request"])
        );
        setEditSubcontractorStatus(QUERY_STATUS.ERROR);
        console.error("strUpdate", e);
        throw e;
      }
    },
    [userScope]
  );

  const deleteSubcontractor = useCallback(
    async (id) => {
      setDeleteSubcontractorStatus(QUERY_STATUS.LOADING);
      try {
        await withUserScopeCheck({
          callback: async () => {
            await deleteSubcontractorQuery({ id, userScope: userScope });
          },
          scope: userScope,
        });
        setDeleteSubcontractorStatus(QUERY_STATUS.SUCCESS);
        setSubcontractorById({});
        setSubcontractorBySlug({});
      } catch (e) {
        setDeleteSubcontractorStatus(QUERY_STATUS.ERROR);
        console.error("deleteSubcontractor", e);
        throw e;
      }
    },
    [userScope]
  );

  const getSubcontractorsBySubcontractorCompany = useCallback(
    async (id) => {
      try {
        setSubcontractorsBySubcontractorCompany(
          await withUserScopeCheck({
            callback: async () => {
              return await getSubcontractorsBySubcontractorCompanyQuery({
                id,
                userScope: userScope,
              });
            },
            scope: userScope,
          })
        );
      } catch (e) {
        console.error("getSubcontractorsBySubcontractorCompany", e);
        throw e;
      }
    },
    [userScope]
  );

  const values = useMemo(
    () => ({
      deleteSubcontractor,
      createdSubcontractorStatus,
      createdSubcontractor,
      setCreatedSubcontractorStatus,
      setCreatedSubcontractor,
      subcontractorCreate,
      subcontractorById,
      subcontractorByIdGetter,
      headerRoles,
      subStrUpdate,
      setHeaderRoles,
      editSubcontractorStatus,
      setEditSubcontractorStatus,
      displaySubcontractorStatus,
      setSubcontractorBySlug,
      getSubcontractorBySlug,
      subcontractorBySlug,
      getSubcontractorsBySubcontractorCompany,
      subcontractorsBySubcontractorCompany,
      subcontractorAccessRights,
      setSubcontractorAccessRights,
      apiErrorResponse,
      deleteSubcontractorStatus,
      setDeleteSubcontractorStatus,
    }),
    [
      setSubcontractorBySlug,
      subcontractorBySlug,
      deleteSubcontractor,
      createdSubcontractorStatus,
      createdSubcontractor,
      setCreatedSubcontractorStatus,
      setCreatedSubcontractor,
      subcontractorCreate,
      subcontractorById,
      subcontractorByIdGetter,
      headerRoles,
      subStrUpdate,
      setHeaderRoles,
      editSubcontractorStatus,
      setEditSubcontractorStatus,
      displaySubcontractorStatus,
      subcontractorsBySubcontractorCompany,
      subcontractorAccessRights,
      setSubcontractorAccessRights,
      apiErrorResponse,
      deleteSubcontractorStatus,
      setDeleteSubcontractorStatus,
    ]
  );
  return <SubcontractorContext.Provider value={values} {...props} />;
};
